import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { GlobalContext } from 'components/context'

const MobileCrumb = ({ previousPath, previousPage }) => {
  const { t } = React.useContext(GlobalContext)

  const page = previousPage ? t(`pages.${previousPage}.title`) : t('mypage.title')

  return (
    <p className={'hidden desktop'}>
      <Crumb
        path={previousPath}
        txt={t('navigation.breadcrumb-mobile', { page: page })}
      />
    </p>
  )
}

MobileCrumb.propTypes = {
  previousPath: PropTypes.string.isRequired,
  previousPage: PropTypes.string,
}

const IGNORED = [
  'pt-package',
]

const DummyTopLevels = ['tips', 'missing-out-on', 'confirm']
const DummyMidLevels = {
  access: ['nfc', 'rfid', 'lost_chip'],
}

const filterPath = (path, exceptions) => {
  const result = []
  for (let i = 0; i < path.length; i++) {
    const current = path[i]
    const next = path[i + 1]

    if (exceptions[current]?.includes(next)) continue

    result.push(current)
  }
  return result
}

const BreadCrumbs = ({ location }) => {
  let parts = location.pathname.split('/')

  // The cancellation process has been extended with nested paths to support
  // multiple steps. Filter out the nested paths to keep the breadcrumb simple.
  parts = parts.filter(p => !DummyTopLevels.includes(p))

  // make a copy of parts for phrases, as these should follow the full path
  const phraseParts = Array.from(parts)

  // The access process has been extended to support alternative steps nested
  // under 'access'. Filter out the grouping paths to keep the breadcrumb simple.
  parts = filterPath(parts, DummyMidLevels)

  let currentPage = parts.pop()

  if (IGNORED.indexOf(currentPage) > -1) {
    return null
  }

  const previousPath = parts.join('/')
  let previousPage = parts[parts.length - 1]

  // Just a little hack to pretend that /payment/payment-method-updated is the same as /payment
  // We redirect to this after updating creditcard
  if (previousPage === 'payment' && currentPage === 'payment-method-updated') {
    currentPage = parts.pop()
    previousPage = parts[parts.length - 1]
  }

  // Remove the first one that is just an empty string
  if (parts[0] === '') {
    parts.shift()
    phraseParts.shift()
  }

  return (
    <>
      <DesktopCrumbs {...{ parts, phraseParts, currentPage }} />
      <MobileCrumb {...{ previousPath, previousPage }} />
    </>
  )
}

BreadCrumbs.propTypes = {
  location: PropTypes.object.isRequired,
}

const Crumb = ({ path, txt }) =>
  <span>
    <button className={'link-button back'}>
      <Link to={path}>
        <span className={'hidden desktop'}>&lsaquo;&nbsp;</span>
        { txt }
      </Link>
    </button>
    <span className={'hidden phone'}>&nbsp;&rsaquo;&nbsp;</span>
  </span>

Crumb.propTypes = {
  path: PropTypes.string.isRequired,
  txt: PropTypes.string.isRequired,
}

const DesktopCrumbs = ({ parts, phraseParts, currentPage }) => {
  const { t } = React.useContext(GlobalContext)

  // To get the correct phrase path for the url path, we find the currentPage
  // element in phraseParts and join the parts upto that element
  const phrasePath = (currentPage) => {
    const phraseIndex = phraseParts.indexOf(currentPage)
    const phrase = phraseParts.slice(0, phraseIndex + 1) // +1 so we include currentPage

    if (phrase.length === 0) return currentPage.replaceAll('-', '_')

    return phrase.join('.').replaceAll('-', '_')
  }

  const crumb = (part, partIndex, parts) => {
    const path = ['', ...parts.slice(0, partIndex + 1)].join('/')

    return (
      <Crumb
        key={path}
        path={path}
        txt={ t([`pages.${phrasePath(part)}.title`, 'crumb'], { crumb: part }) }
      />
    )
  }

  return (
    <p className={'hidden phone'}>
      <Crumb
        path={'/'}
        txt={ t('mypage.title') }
      />
      { parts.map(crumb) }{t([`pages.${phrasePath(currentPage)}.title`, 'crumb'], { crumb: currentPage })}
    </p>
  )
}

DesktopCrumbs.propTypes = {
  parts: PropTypes.array.isRequired,
  phraseParts: PropTypes.array.isRequired,
  currentPage: PropTypes.string.isRequired,
}

export default BreadCrumbs
